import React from 'react';
import Markdown, { RuleType } from 'markdown-to-jsx';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';

// Only ordinary links: no javascript:, data: or other schemes from user-written text
const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

const hostOf = (url) => { try { return new URL(url).hostname.toLowerCase(); } catch { return ''; } };

// Otable itself: the app and its API (an image pointing there would make every viewer's browser send a
// request with their login cookies)
const OWN_HOSTS = new Set(['otable.net', hostOf(process.env.REACT_APP_API_BASE_URL || ''),
  typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : ''].filter(Boolean));
const isOwnHost = (host) => OWN_HOSTS.has(host) || host.endsWith('.otable.net');

const textOf = (node) => (Array.isArray(node) ? node.map(textOf).join('')
  : typeof node === 'string' || typeof node === 'number' ? String(node) : node?.props ? textOf(node.props.children) : '');

// A mention written by the post composer: a person ([@Ayşe Demir](/profile/<id>/)), a public consortium
// (/consortium/<id>/) or an organization (/organization/<id>/) — its page, in this tab
const MENTION_HREF = /^\/(profile|consortium|organization)\/[0-9a-fA-F-]{32,36}\/?$/;

function SafeLink({ href, children, ...rest }) {
  if (href && MENTION_HREF.test(href)) {
    return (
      <Link component={RouterLink} to={href} underline="hover" className="mention" onClick={(e) => e.stopPropagation()}
        sx={{ fontWeight: 600, color: 'primary.main' }}>
        {children}
      </Link>
    );
  }
  if (!href || !SAFE_HREF.test(href)) return <span>{children}</span>;
  // Where a link really goes is always visible: the address on hover, and the site after the text of a
  // link leaving Otable whose text is not the address itself (so "otable.net/login" cannot hide another site)
  const host = hostOf(href).replace(/^www\./, '');
  const text = textOf(children).trim();
  const showHost = host && !isOwnHost(hostOf(href)) && text !== href && text.replace(/^https?:\/\//, '').replace(/\/$/, '') !== href.replace(/^https?:\/\//, '').replace(/\/$/, '');
  // a link opens the page only, not whatever the surrounding text does on click (e.g. a post opening its editor)
  return (
    <>
      <Link {...rest} href={href} target="_blank" rel="noopener noreferrer" underline="hover" title={href}
        onClick={(e) => e.stopPropagation()}>{children}</Link>
      {showHost && (
        <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8em', ml: 0.4, whiteSpace: 'nowrap' }}>({host})</Box>
      )}
    </>
  );
}

// Images only where asked for (posts), only over https, opening full size in a new tab
const SAFE_IMG = /^https:\/\//i;
// Every image fits in this box (never wider than the text on narrow screens), keeping its proportions
const IMG_MAX_WIDTH = 560;
const IMG_MAX_HEIGHT = 360;

export function SafeImage({ src, alt, title }) {
  if (!src || !SAFE_IMG.test(src) || isOwnHost(hostOf(src))) return null;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
      <Box component="img" src={src} alt={alt || ''} title={title} loading="lazy" decoding="async" referrerPolicy="no-referrer"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        sx={{ display: 'block', width: 'auto', height: 'auto', maxWidth: `min(100%, ${IMG_MAX_WIDTH}px)`, maxHeight: IMG_MAX_HEIGHT,
          objectFit: 'contain', my: 1, borderRadius: 1 }} />
    </a>
  );
}

// Sized from the body text's size, not "em": the surrounding font is the app's tiny root size, so "1.35em" came
// out no bigger than the paragraphs and a heading looked like plain bold text
const heading = (scale) => ({ children }) => (
  <Typography component="div" sx={{ fontWeight: 700, lineHeight: 1.35, mt: 1.75, mb: 0.75, '&:first-of-type': { mt: 0 },
    fontSize: (t) => `calc(${t.typography.body1.fontSize} * ${scale})` }}>
    {children}
  </Typography>
);

const OPTIONS = {
  disableParsingRawHTML: true,          // user text: markdown only, never HTML
  forceBlock: true,
  // a heading needs a space after its #s ("# Results"), as in CommonMark: "#climate" stays text (a hashtag)
  enforceAtxHeadings: true,
  overrides: {
    h1: heading(1.6), h2: heading(1.4), h3: heading(1.2), h4: heading(1.1), h5: heading(1), h6: heading(1),
    p: { component: ({ children }) => <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.7 }}>{children}</Typography> },
    a: { component: SafeLink },
    // Lists and tables go through Typography so they get the page's text size (the app's root size is tiny)
    ul: { component: ({ children }) => <Typography component="ul" variant="body1" sx={{ pl: 3, my: 1, '& li': { mb: 0.5, lineHeight: 1.65 }, '& ul, & ol': { my: 0.5 } }}>{children}</Typography> },
    ol: { component: ({ children }) => <Typography component="ol" variant="body1" sx={{ pl: 3, my: 1, '& li': { mb: 0.5, lineHeight: 1.65 }, '& ul, & ol': { my: 0.5 } }}>{children}</Typography> },
    blockquote: { component: ({ children }) => (
      <Box component="blockquote" sx={{ m: 0, my: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'divider', color: 'text.secondary', fontStyle: 'italic' }}>{children}</Box>
    ) },
    code: { component: ({ children }) => (
      <Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.9em', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>{children}</Box>
    ) },
    pre: { component: ({ children }) => (
      <Typography component="pre" variant="body2" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', '& code': { bgcolor: 'transparent', p: 0 } }}>{children}</Typography>
    ) },
    hr: { component: () => <Box component="hr" sx={{ border: 0, borderTop: '1px solid', borderColor: 'divider', my: 2 }} /> },
    table: { component: ({ children }) => (
      <Box sx={{ overflowX: 'auto', my: 1.5 }}>
        <Typography component="table" variant="body2" sx={{ borderCollapse: 'collapse', '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.25, py: 0.75, textAlign: 'left' }, '& th': { bgcolor: 'action.hover', fontWeight: 600 } }}>{children}</Typography>
      </Box>
    ) },
    img: { component: () => null },     // no remote images from user text
  },
};

// Hashtags in posts: # and a word with at least one letter ("#climate", "#COP29", "#iklim_değişikliği"), not
// "#1", "C#", "page#part" or a link's "/#section"; code is left alone (it is not a text node)
const HASHTAG = /(^|[^\p{L}\p{N}_&/#])(#[\p{L}\p{N}_]*\p{L}[\p{L}\p{N}_]*)/u;

function withHashtags(text) {
  const out = [];
  let rest = text;
  let m;
  while ((m = HASHTAG.exec(rest))) {
    const start = m.index + m[1].length;
    if (start) out.push(rest.slice(0, start));
    out.push(<Box component="span" key={out.length} className="hashtag" sx={{ color: 'primary.main', fontWeight: 500 }}>{m[2]}</Box>);
    rest = rest.slice(start + m[2].length);
  }
  if (rest) out.push(rest);
  return out;
}

const hashtagRule = (next, node, _renderChildren, state) => (
  node.type === RuleType.text && HASHTAG.test(node.text)
    ? <React.Fragment key={state.key}>{withHashtags(node.text)}</React.Fragment>
    : next()
);

const optionsFor = (images, hashtags) => ({
  ...OPTIONS,
  ...(images && { overrides: { ...OPTIONS.overrides, img: { component: SafeImage } } }),
  ...(hashtags && { renderRule: hashtagRule }),
});
const OPTION_SETS = [[false, false], [true, false], [false, true], [true, true]]
  .reduce((acc, [i, h]) => ({ ...acc, [`${i}${h}`]: optionsFor(i, h) }), {});

/** A lab's description (or a post), written in markdown. IMAGES: show ![alt](https://…) images;
 *  HASHTAGS: show #tags as tags. */
export default function LabMarkdown({ children, images = false, hashtags = false }) {
  return <Box sx={{ '& > *:last-child': { mb: 0 } }}><Markdown options={OPTION_SETS[`${images}${hashtags}`]}>{children || ''}</Markdown></Box>;
}

/** Plain text for cards and previews: the markdown syntax removed. */
export function markdownToText(md = '') {
  return md
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/<[^>]*>/g, ' ')                                   // raw HTML is never rendered: drop it here too
    .replace(/!\[[^\]]*]\((?:[^()]|\([^()]*\))*\)/g, '')
    .replace(/\[([^\]]+)]\((?:[^()]|\([^()]*\))*\)/g, '$1')    // [text](url), also urls with (…) inside
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+/gm, '')
    .replace(/(\*\*|__|\*|_|`|~~)/g, '')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
